import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { listSessionFeedback, submitFeedback, type Feedback, type FeedbackSubmit } from './api';

export const feedbackKeys = {
  all: ['feedback'] as const,
  session: (vertexSessionId: string) => [...feedbackKeys.all, 'session', vertexSessionId] as const,
};

export function useSessionFeedback(vertexSessionId: string | undefined) {
  return useQuery({
    queryKey: feedbackKeys.session(vertexSessionId ?? ''),
    queryFn: ({ signal }) => listSessionFeedback(vertexSessionId!, signal),
    enabled: !!vertexSessionId,
    // A conversation not yet registered in sreg-api has no feedback to show.
    meta: { silent: true },
    retry: false,
  });
}

/** The caller's rating for one answer, if any. */
export function useMessageFeedback(vertexSessionId: string | undefined, messageRef: string): Feedback | undefined {
  return useSessionFeedback(vertexSessionId).data?.find((f) => f.messageRef === messageRef);
}

export function useSubmitFeedback() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: FeedbackSubmit) => submitFeedback(body),
    onSuccess: (saved) =>
      qc.setQueryData<Feedback[]>(feedbackKeys.session(saved.vertexSessionId), (rows = []) => [
        ...rows.filter((r) => r.messageRef !== saved.messageRef),
        saved,
      ]),
  });
}
